import React from 'react';

export interface BudgetGaugeProps {
  used: number;
  total: number;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const getBudgetColor = (percentage: number): string => {
  if (percentage >= 90) return '#ef4444'; // red - critical
  if (percentage >= 70) return '#f59e0b'; // amber - warning
  return '#10b981'; // green - healthy
};

const getBudgetLabel = (percentage: number): string => {
  if (percentage >= 90) return 'Critical';
  if (percentage >= 70) return 'Warning';
  return 'Healthy';
};

const sizeConfig = {
  sm: {
    size: 100,
    strokeWidth: 8,
    fontSize: 'text-lg',
    labelSize: 'text-xs',
  },
  md: {
    size: 140,
    strokeWidth: 10,
    fontSize: 'text-2xl',
    labelSize: 'text-sm',
  },
  lg: {
    size: 180,
    strokeWidth: 12,
    fontSize: 'text-3xl',
    labelSize: 'text-base',
  },
};

export const BudgetGauge: React.FC<BudgetGaugeProps> = ({
  used,
  total,
  size = 'md',
  className = '',
}) => {
  const config = sizeConfig[size];
  const percentage = total > 0 ? Math.min((used / total) * 100, 100) : 0;
  const color = getBudgetColor(percentage);
  const label = getBudgetLabel(percentage);

  // SVG circle calculations (semi-circle gauge)
  const center = config.size / 2;
  const radius = (config.size - config.strokeWidth) / 2 - 10;
  const circumference = Math.PI * radius; // Half circle
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const formatCurrency = (value: number): string => {
    if (value >= 1) {
      return `$${value.toFixed(2)}`;
    }
    return `$${value.toFixed(4)}`;
  };

  return (
    <div className={`inline-flex flex-col items-center ${className}`}>
      <svg
        width={config.size}
        height={config.size / 2 + 20}
        className="overflow-visible"
      >
        {/* Background arc */}
        <path
          d={`M ${config.strokeWidth / 2 + 10} ${center} A ${radius} ${radius} 0 0 1 ${config.size - config.strokeWidth / 2 - 10} ${center}`}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={config.strokeWidth}
          strokeLinecap="round"
        />
        {/* Progress arc */}
        <path
          d={`M ${config.strokeWidth / 2 + 10} ${center} A ${radius} ${radius} 0 0 1 ${config.size - config.strokeWidth / 2 - 10} ${center}`}
          fill="none"
          stroke={color}
          strokeWidth={config.strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-500 ease-out"
        />
        {/* Percentage text */}
        <text
          x={center}
          y={center - 5}
          textAnchor="middle"
          dominantBaseline="central"
          className={`${config.fontSize} font-bold`}
          fill={color}
        >
          {percentage.toFixed(0)}%
        </text>
      </svg>
      <div className="mt-1 text-center">
        <div className={`${config.labelSize} font-semibold`} style={{ color }}>
          {label}
        </div>
        <div className={`${config.labelSize} text-gray-500`}>
          {formatCurrency(used)} / {formatCurrency(total)}
        </div>
      </div>
    </div>
  );
};

BudgetGauge.displayName = 'BudgetGauge';

export default BudgetGauge;
